"use client";

import { unwrapResult } from "@reduxjs/toolkit";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import {
  InlineInfoBox,
  Modal,
  TagSearchField,
} from "staysure-component-library";

import { getQuoteReviewSummaryEditSection } from "@/app/common-utils/edit-quote-review-summary-flow.utils";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import FadeAnimation from "@/components/atoms/animation/fade-animation";
import TransformAnimation from "@/components/atoms/animation/transform-animation";
import ButtonGroup from "@/components/atoms/button-group/button-group";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import { MagnoliaImage } from "@/components/atoms/image/image";
import Loader from "@/components/atoms/loader/loader";
import ConfirmationModal from "@/components/atoms/modal/confirmation-modal";
import RichText from "@/components/atoms/rich-text/rich-text";
import UnSavedChangesWarningMessage from "@/components/organisms/un-saved-changes-warning-message/un-saved-changes-warning-message";
import withApplication from "@/components/with-application";
import withBrand from "@/components/with-brand";
import {
  AnnualMultiTrip,
  CoverEdit,
  DestinationEdit,
  Europe,
  FinishEdit,
  QuestionThree,
  QuestionTwo,
  RenewalPage,
  SingleTrip,
  Worldwide,
} from "@/config/app-constant";
import { Routes } from "@/config/page-view-constants";
import { PRODUCT_DOMAIN } from "@/constants";
import { useSearchCountry } from "@/hooks/api/address-api/address-api";
import useDebounce from "@/hooks/debounce/debounce";
import useGtmPopup from "@/hooks/gtm/use-gtm-popup";
import useGtmQuestionSet from "@/hooks/gtm/use-gtm-question-set";
import { useStickyFooter } from "@/hooks/use-media-query/use-media-query-enhanced";
import { useQuoteDisabled } from "@/hooks/use-quote-disabled/use-quote-disabled";
import useResetStateTravelerEdit from "@/hooks/use-reset-state-traveler-edit/user-reset-state-traveler-edit";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { useQuestionsState } from "@/providers/questions-state-provider";
import { useAppDispatch } from "@/store/hook";
import {
  saveQuestionDataBFF,
  sessionCheck,
  updateQuoteOnQuestion,
} from "@/store/slices/quote/quote.action";
import { setQuestionData } from "@/store/slices/quote/quote.slice";
import { QuoteCountryType } from "@/types/AddressTypes";
import { OptionListProps } from "@/types/LocationFromType";
import { LocationToContainerProps } from "@/types/LocationToType";
import { STCountryArray, STCountryObj } from "@/types/QuestionPayloadType";
import { QuotePayload } from "@/types/quote/quote-data";
import { TripDetails } from "@/types/TripDetails";
import selectLoationHookFlash, {
  modifyPayloadSave,
} from "@/utils/common.utils";
import scrollInputToTop from "@/utils/input-anchoring";
import { getRouterPathBackButton } from "@/utils/page-routing.utils";
import { travelFromCountryData } from "@/utils/question-location-from.utils";
import {
  useFilteredTranslatedAnswersQTwoWhereGoing,
  useFindTranslatedCountryIdByValue,
  useTranslatedAnswersQTwoEurope,
  useTranslatedAnswersQTwoWorldwide,
  useFindTranslatedIdByEuropeValue,
  useFindTranslatedIdByWorldWideValue,
  findAnswerById,
  findDuplicatesByProperty,
  findFCDOCountry,
  generateTagSearchArray,
  getQuestionTwoState,
  findAnswerWorldwideById,
  findAnswerForEuropeId,
} from "@/utils/question-location-to.utils";
import {
  handleScrollToRef,
  handleScrollToRefAnyoneVisiting,
  smoothScrollToRef,
} from "@/utils/scrollUtils";
import { checkCurentLocation } from "@/utils/travel-date.utils";

import styleComponent from "./location-to-container.module.css";
import ActionButton from "../../action-button/action-button";
import { TextButton } from "../../cta-button/cta-buttons";
import ConfirmationsModal from "../../quote-section/edit-quote-section/confirmation-modal";
import editQuoteStyle from "../../quote-section/edit-quote-section/edit-quote.module.css";
import BackToRenewal from "../../renewals/back-to-renewal/back-to-renewal";
import LocationFromContainer from "../location-from/location-from-container";
import styles from "../question.module.css";

const initialPayload: QuotePayload = {
  type: "IntermediateData",
  channel: "ONLINE",
  attributes: {
    domain: PRODUCT_DOMAIN,
    lastVisitedPage: "test",
  },
};

function LocationToContainer(props: Readonly<LocationToContainerProps>) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { current } = useWizard();
  const { t } = useWizardTranslation();

  const [editModal, setEditModal] = useState(false);
  const [valueFilter, setValueFilter] = useState<string | null>(null);
  const [selectedSTCountry, setSelectedSTCountry] = useState<[] | undefined>(
    undefined
  );
  const [sameCountrySelected, setSameCountrySelected] = useState<
    [] | undefined
  >(undefined);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [selectedAMTEuropeWorldwide, setSelectedAMTEuropeWorldwide] = useState<
    string | undefined
  >(undefined);
  const [addressList, setAddressList] = useState<OptionListProps[] | undefined>(
    undefined
  );
  const [fromCountry, setFromCountry] = useState<string | undefined>(undefined);
  const [fromCountryCode, setFromCountryCode] = useState<string | undefined>(
    undefined
  );
  const [buttonDisableFromLocation, setButtonDisableFromLocation] =
    useState<boolean>(true);
  const [payload, setPayload] = useState<QuotePayload>(initialPayload);
  const [questionState, setQuestionState] = useState<{
    fromCountrySelected: string | null;
    toCountrySelected: string | null;
    valueSelected: string | null;
    countryArray: [];
  }>({
    fromCountrySelected: null,
    toCountrySelected: null,
    valueSelected: null,
    countryArray: [],
  });
  const [freeSoloTag, setFreeSoloTag] = useState<boolean>(true);
  const [showSameCountryModal, setShowSameCountryModal] = useState(false);
  const [isDisable, setIsDisable] = useState<boolean>(false);
  const [isSubmitBtnClicked, setIsSubmitBtnClicked] = useState<boolean>(false);
  const [isClient, setIsClient] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUKModal, setSelectedUKModal] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [fcdoCountry, setFcdoCountry] = useState<boolean>(false);
  const [isOpenUnsavedChangesModal, setIsOpenUnsavedChangesModal] =
    useState(false);
  const [optionListText, setOptionListText] = useState<string>("");
  const locationToHeaderRef = useRef<HTMLHeadingElement | null>(null);
  const locationToSTHeaderRef = useRef<HTMLHeadingElement | null>(null);
  const worldwideHeaderRef = useRef<HTMLHeadingElement | null>(null);
  const europeHeaderRef = useRef<HTMLHeadingElement | null>(null);
  const isStickyFooter = useStickyFooter();
  const actionButtonRef = useRef<HTMLDivElement | null>(null);

  const {
    application = "quote",
    tripDetails,
    quoteResponse,
    quoteLoading,
    brand,
    formRef,
    ctaRef,
    selectedpolicyType,
    pageContent,
    toLocationCountryDetails,
    expatText,
    expatStatus,
    usagePage,
    buttonDisable,
    setToLocationCountryDetails,
  } = props;

  const {
    questionTwoText,
    questionTwoPlaceHolder,
    modalTitleText,
    modalSubText,
    modalConfirmBtnText,
    modalCancelBtnText,
    sameCountryModalHeaderText,
    sameCountryModalSubText,
    agreeBtnText,
    closeBtnText,
    cancelEditingBtnTextWorldWide,
    cancelEditingBtnTextEurope,
    finishEditingBtnText = FinishEdit,
    fcdoCountryHeaderText,
    sameCountryHeaderText,
    fcdoCountrySubTextText,
    sameCountrySubTextText,
    fcdoCountryBtnText,
    sameCountryBtnText,
    backToRenewalsText,
    popularCountryHeaderText,
    popularCountryList,
  } = pageContent;

  const locationFromContent = pageContent?.locationFrom?.["0"];

  const {
    selected: stateToLocation,
    setSelectedState,
    setSelectedStateTwo,
  } = useQuestionsState();
  const { resetQuestionData } = useResetStateTravelerEdit();
  const dispatch = useAppDispatch();
  const isQuoteDisabled = useQuoteDisabled();

  const editPage = searchParams.get(DestinationEdit);
  const coverEdit = searchParams.get(CoverEdit);
  const { toCountrySelected, valueSelected, countryArray } = questionState;
  const debouncedSearchTerm = useDebounce(valueFilter, 600);

  // Add translated hooks after valueSelected is available
  const translatedEuropeAnswers = useTranslatedAnswersQTwoEurope();
  const translatedWorldwideAnswers = useTranslatedAnswersQTwoWorldwide();
  const europeSelectedAnswer = useFindTranslatedIdByEuropeValue(
    valueSelected ?? ""
  );
  const worldwideSelectedAnswer = useFindTranslatedIdByWorldWideValue(
    valueSelected ?? ""
  );

  // Translation hooks
  const filteredTranslatedAnswers = useFilteredTranslatedAnswersQTwoWhereGoing(
    current.brandSpecificParams?.locationToCountries ?? []
  );
  const findTranslatedCountryId = useFindTranslatedCountryIdByValue(
    toCountrySelected ?? ""
  );

  const [addresses, loading, fetchAddress]: [
    QuoteCountryType[],
    boolean,
    () => void,
  ] = useSearchCountry(debouncedSearchTerm);
  const toLocationData = selectLoationHookFlash(
    payload?.attributes?.tripDetails ?? undefined
  );
  const { ctaButtonText } = pageContent;
  const editFinishStyle =
    editPage && application !== RenewalPage
      ? editQuoteStyle.editFinishButton
      : "";

  const getPopupName = () => {
    if (modalOpen) {
      return "UK Destination Confirm";
    }
    if (showSameCountryModal) {
      return sameCountryModalHeaderText || "Information";
    }
    if (fcdoCountry) {
      return fcdoCountryHeaderText;
    }
    return sameCountryHeaderText ?? "Information";
  };

  useGtmPopup({
    tripType: QuestionThree,
    isPopupOpen: modalOpen || showSameCountryModal || isModalOpen,
    popupName: getPopupName(),
  });

  useGtmQuestionSet({
    pageTitle: QuestionThree,
    tripType: selectedpolicyType,
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    destination: toLocationData.destination,
    destinationAdditions: toLocationData.destinationAdditions,
    travelFrom: payload?.attributes?.tripDetails?.fromLocation ?? "",
    travelGroupType: undefined,
    isSubmitBtnClicked,
  });

  useEffect(() => {
    // This code will run only on the client side
    setIsClient(true);
  }, []);

  interface CountryData {
    countryName: string;
    countryCode: string;
    displayName: string;
    countryImage: MagnoliaImage | string;
  }
  const processCountryList = (countryList: Record<string, CountryData>) => {
    return Object.values(countryList).map((countryData) => {
      const { countryName, countryCode, displayName, countryImage } =
        countryData;
      const popularCountryImage: MagnoliaImage =
        typeof countryImage === "string"
          ? {
              "@id": "",
              "@link": countryImage,
              "@name": "",
              "@path": "",
              "@type": "",
              "@uuid": "",
            }
          : countryImage;

      const srcImage = `${process.env.NEXT_PUBLIC_MGNL_HOST}/dam/${popularCountryImage["@id"]}${popularCountryImage["@path"]}`;

      return {
        displayLabel: displayName,
        parentCode: countryCode,
        parentName: countryName,
        countryImage: srcImage,
      };
    });
  };

  const modifyPayloadToLocation = (
    fromLocation?: string | undefined,
    toLocation?: string | undefined,
    area?: string | undefined,
    stCountryList?: STCountryArray
  ) => {
    if (area) {
      // eslint-disable-next-line no-param-reassign
      toLocation = undefined;
    }
    if (toLocation) {
      // eslint-disable-next-line no-param-reassign
      area = undefined;
    }
    const tripDetailsModifiled: TripDetails = {
      ...tripDetails,
      ...{ fromLocation },
      ...{ toLocation },
      ...{ area },
      ...{ toLocationCountryDetails: stCountryList },
    };
    setPayload(modifyPayloadSave(quoteResponse, tripDetailsModifiled));
  };

  useEffect(() => {
    if (stateToLocation && selectedpolicyType) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data: any = getQuestionTwoState(
        stateToLocation as string,
        selectedpolicyType,
        toLocationCountryDetails
      );
      setQuestionState(data);
    }
  }, [selectedpolicyType, stateToLocation, toLocationCountryDetails]);

  useEffect(() => {
    if (fromCountry) {
      const countryCode = travelFromCountryData.find(
        (country) => country.displayName === fromCountry
      );
      setFromCountryCode(countryCode?.code);

      if (selectedpolicyType === AnnualMultiTrip) {
        modifyPayloadToLocation(
          countryCode?.code as string,
          undefined,
          stateToLocation as string
        );
      } else {
        modifyPayloadToLocation(
          countryCode?.code as string,
          stateToLocation as string,
          undefined,
          toLocationCountryDetails
        );
      }
    } else if (selectedpolicyType === AnnualMultiTrip) {
      modifyPayloadToLocation(
        quoteResponse?.attributes?.tripDetails?.fromLocation ?? undefined,
        undefined,
        stateToLocation as string
      );
    } else {
      modifyPayloadToLocation(
        quoteResponse?.attributes?.tripDetails?.fromLocation ?? undefined,
        stateToLocation as string,
        undefined,
        toLocationCountryDetails
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromCountry, selectedpolicyType, stateToLocation, undefined]);

  useEffect(() => {
    if (toCountrySelected) {
      setSelectedAMTEuropeWorldwide(toCountrySelected);
    }
  }, [toCountrySelected]);

  useEffect(() => {
    if (countryArray) {
      setSelectedSTCountry(countryArray);
    }
  }, [countryArray]);

  useEffect(() => {
    if (valueFilter) {
      setAddressList(generateTagSearchArray(addresses));
      setOptionListText(
        addresses.length === 0 ? t("no_result_found", "No result found") : ""
      );
    } else {
      setAddressList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses]);

  useEffect(() => {
    if (debouncedSearchTerm && debouncedSearchTerm.length > 2) {
      setFreeSoloTag(loading);
    } else {
      setFreeSoloTag(true);
    }
  }, [debouncedSearchTerm, loading]);

  useEffect(() => {
    if (valueFilter) {
      fetchAddress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const selectedEurope = (answerNo: number) => {
    const qtAnswer = findAnswerForEuropeId(answerNo)?.value;

    if (qtAnswer === stateToLocation) {
      setQuestionState({ ...questionState, valueSelected: null });
      setSelectedState(Europe);
      return;
    }

    setSelectedState(qtAnswer ?? "");
    modifyPayloadToLocation(fromCountryCode, undefined, qtAnswer ?? "");
  };

  const selectedWorldWide = (answerNo: number) => {
    const qtAnswer = findAnswerWorldwideById(answerNo)?.value;

    if (qtAnswer === stateToLocation) {
      setQuestionState({ ...questionState, valueSelected: null });
      setSelectedState(Worldwide);
      return;
    }

    setSelectedState(qtAnswer ?? "");
    modifyPayloadToLocation(undefined, qtAnswer ?? "");
    setSelectedState(qtAnswer ?? "");
    modifyPayloadToLocation(fromCountryCode, undefined, qtAnswer ?? "");
  };

  const selectedCountry = (answerNo: number) => {
    const selectedCountryAMT =
      findAnswerById(answerNo, current.brandSpecificParams?.locationToCountries)
        ?.value ?? "";
    if (selectedCountryAMT === toCountrySelected) {
      setSelectedUKModal(null);
      setSelectedAMTEuropeWorldwide(undefined);
      setQuestionState({
        ...questionState,
        toCountrySelected: null,
        valueSelected: null,
      });
      setSelectedState("");
      return;
    }

    if (
      selectedCountryAMT ===
        (current.brandSpecificParams?.fromCountry as string) &&
      current.uiElementControls?.locationFrom
    ) {
      if (
        !toCountrySelected ||
        toCountrySelected ===
          (current.brandSpecificParams?.fromCountry as string)
      ) {
        setSelectedUKModal(null);
      }

      setModalOpen(true);
    } else if (
      selectedCountryAMT === Europe &&
      !current.uiElementControls?.locationFrom
    ) {
      if (!toCountrySelected || toCountrySelected === Europe) {
        setSelectedUKModal(null);
      }
      setModalOpen(true);
    } else {
      setSelectedAMTEuropeWorldwide(selectedCountryAMT);
      setSelectedState(selectedCountryAMT);
      modifyPayloadToLocation(fromCountryCode, undefined, selectedCountryAMT);
    }
  };

  const modifyModalButtonClick = (buttonClick: boolean) => {
    setModalOpen(!modalOpen);
    if (buttonClick) {
      if (current.uiElementControls?.locationFrom) {
        const element1 = document.getElementById("paragaph-test-id");
        if (element1) {
          handleScrollToRefAnyoneVisiting(actionButtonRef, !isStickyFooter);
        } else {
          setTimeout(() => {
            smoothScrollToRef(
              selectedAMTEuropeWorldwide === Europe
                ? worldwideHeaderRef
                : europeHeaderRef
            );
          }, 300);
        }
      } else {
        handleScrollToRef(actionButtonRef, !isStickyFooter);
      }

      setSelectedUKModal(1);
      const selectedVal = current.uiElementControls?.locationFrom
        ? (current.brandSpecificParams?.fromCountry as string)
        : Europe;
      setSelectedAMTEuropeWorldwide(selectedVal);
      setSelectedState(selectedVal);
      modifyPayloadToLocation(fromCountryCode, undefined, selectedVal);
    } else if (
      !toCountrySelected ||
      toCountrySelected === (current.brandSpecificParams?.fromCountry as string)
    ) {
      setSelectedUKModal(null);
      setSelectedAMTEuropeWorldwide(undefined);
      setSelectedState("");
      modifyPayloadToLocation(
        fromCountryCode,
        undefined,
        current.brandSpecificParams?.fromCountry as string
      );
    } else {
      // eslint-disable-next-line no-lonely-if
      if (
        !toCountrySelected ||
        toCountrySelected ===
          (current.brandSpecificParams?.fromCountry as string) ||
        toCountrySelected === Europe
      ) {
        setSelectedUKModal(null);
        setSelectedAMTEuropeWorldwide(undefined);
        setSelectedState("");
        modifyPayloadToLocation(undefined, undefined);
      }
    }
  };

  const handleScrollDown = () => {
    handleScrollToRef(actionButtonRef, !isStickyFooter);
  };

  const handleCancelEdit = () => {
    setEditModal(true);
  };

  const fontConfig = [
    {
      brand: "staysure",
      fontWeightClass: "font-bold",
      fontColorClass: "text-text-accent-1-primary",
      fontFamilyClass: "font-body-font",
    },
    {
      brand: "avanti",
      fontWeightClass: "font-bold",
      fontColorClass: "text-text-body",
      fontFamilyClass: "font-body-font",
    },
  ];

  function getFontClasses(lBrand: string | undefined): string {
    let fontClasses = "";
    fontConfig.forEach((config) => {
      if (config.brand === lBrand) {
        fontClasses = `${config.fontColorClass} ${config.fontWeightClass} ${config.fontFamilyClass}`;
      }
    });
    return fontClasses;
  }

  const handleBackButton = () => {
    if (application === "renewal") {
      setIsOpenUnsavedChangesModal(true);
      return;
    }

    dispatch(setQuestionData(payload));
    let path = getRouterPathBackButton(QuestionTwo);
    if (editPage || coverEdit) {
      path = `${path}?${editPage ? DestinationEdit : CoverEdit}=true`;
    }
    router.push(path);
  };

  const handleSubmitButton = async () => {
    if (!buttonDisable) {
      setIsSubmitBtnClicked(true);
      setButtonLoading(true);

      try {
        dispatch(setQuestionData(payload));
        if (editPage && quoteResponse?.referenceId) {
          const result = await dispatch(
            updateQuoteOnQuestion({
              payload: { ...payload, viewPage: Routes.TravellingTo },
              dispatch,
              router,
            })
          );
          const originalPromiseResult = unwrapResult(result);

          if (application === "renewal") {
            router.push("/");
          } else if (originalPromiseResult) {
            let path = t("quote_path", "quote");
            router.push(`/${path}`);
            // router.push(`/${Quote}`);
          }
        } else if (coverEdit && quoteResponse?.referenceId) {
          // Determine navigation based on edit type and origin
          let nextPage = current.edges?.coverEditNext;

          if (coverEdit) {
            // Check if user came from quote-review (sessionStorage is set)
            const editSection = getQuoteReviewSummaryEditSection();

            if (editSection === "cover") {
              // User came from quote-review, use new flow
              nextPage = current?.edges?.coverEditQuoteSummaryNext; // → /cruise/?coverEdit=true
            } else {
              // User came from quote page, use old flow
              nextPage = current?.edges?.coverEditNext; // → /travel-dates/?coverEdit=true
            }
          }
          router.push(nextPage ?? "");
        } else if (!quoteResponse?.referenceId) {
          const questionData = await dispatch(
            saveQuestionDataBFF({
              payload: { ...payload, viewPage: Routes.TravellingTo },
              dispatch,
              router,
            })
          );
          const originalPromiseResult = unwrapResult(questionData);
          if (originalPromiseResult) {
            // router.push(getRouterPathMainButton(QuestionThree));
            router.push(current.next ?? "");
          }
        } else {
          dispatch(sessionCheck({ dispatch, router }));
          // router.push(getRouterPathMainButton(QuestionThree));
          router.push(current.next ?? "");
        }
        setButtonLoading(false);
      } catch {
        setButtonLoading(false);
      }
    }
  };

  const tagSearchFieldOnChange = (data: OptionListProps[]) => {
    let formCountries = "";
    const stCountryArray: STCountryArray = [];

    if (data && data.length > 0) {
      const existFCDOCountry = findFCDOCountry(
        data[data.length - 1].parentCode
      );

      if (!existFCDOCountry) {
        data.forEach((country: OptionListProps, index) => {
          formCountries += `${country?.parentCode}${
            index !== data.length - 1 ? "," : ""
          }`;
          const stCountryObj: STCountryObj = {
            attributes: {
              countryCode: country?.parentCode,
              countryName: country?.parentName,
            },
          };
          stCountryArray.push(stCountryObj);
        });

        setToLocationCountryDetails(stCountryArray);
        modifyPayloadToLocation(
          fromCountryCode,
          formCountries,
          undefined,
          stCountryArray
        );
        setSelectedState(formCountries);
      } else {
        if (existFCDOCountry) {
          data.splice(-1, 1);
          setSelectedSTCountry(data as []);
          setFcdoCountry(true);
        }
        setIsModalOpen(true);
      }
    } else {
      modifyPayloadToLocation(undefined, undefined, undefined);
      setSelectedState("");
    }
  };

  const renderModal = () => {
    return (
      <Modal
        className={`${styleComponent.same_country_modal} md:p-spacing-xxxl p-spacing-l md:max-w-[416px] max-w-[327px] w-full`}
        isOpen={showSameCountryModal}
        onOpenChange={(e: boolean) => setShowSameCountryModal(e)}
        underlayStyles={{
          zIndex: 9999,
          background: "rgba(26, 26, 26, 0.6)",
        }}
        modalStyles={{
          display: "flex",
          background: "white",
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: 8,
        }}
      >
        <div data-testid="modal-data-test-id">
          <h1 className="text-text-title font-header-font md:text-3xl text-[23px] text-center md:leading-[34.1px] leading-[27.6px] md:mb-spacing-s mb-spacing-xs">
            {sameCountryModalHeaderText}
          </h1>
          <div className={styleComponent.text_content}>
            {sameCountryModalSubText}
          </div>
          <div className="flex md:flex-row flex-col">
            <TextButton
              data-testid="location-to-agree-button"
              id="location-to-agree-button"
              hierarchy="primary"
              label={agreeBtnText || "Agree"}
              onClick={() => {
                setShowSameCountryModal(false);
                if (sameCountrySelected && sameCountrySelected.length > 0) {
                  let modifiedValue = [...sameCountrySelected];
                  if (selectedSTCountry) {
                    modifiedValue = selectedSTCountry?.concat(modifiedValue);
                  }
                  setSelectedSTCountry(modifiedValue as []);

                  tagSearchFieldOnChange(modifiedValue as []);
                }
                // setUserAgree(false);
                setIsDisable(true);
                setTimeout(() => {
                  setIsDisable(false);
                }, 100);
              }}
              className="md:mr-2 md:mb-0 mb-2 !h-[48px] md:w-[148px] leading-[18px] w-full text-text-on-primary"
            />
            <TextButton
              data-testid="location-to-close-button"
              id="location-to-close-button"
              hierarchy="secondary"
              label={closeBtnText || "Close"}
              onClick={() => {
                setShowSameCountryModal(false);
                // setUserAgree(true);
                setIsDisable(true);
                setTimeout(() => {
                  setIsDisable(false);
                }, 100);
              }}
              className="!h-[48px] md:w-[148px] leading-[18px] w-full"
            />
          </div>
        </div>
      </Modal>
    );
  };

  const renderQuestionTwoSectionAMT = () => {
    const { amtQuestionOneText } = pageContent;

    const countryId =
      !toCountrySelected || !selectedAMTEuropeWorldwide
        ? selectedUKModal
        : findTranslatedCountryId?.id;
    return (
      <div
        className=" md:mb-spacing-xxl"
        data-testid="multi-trip-support-content"
      >
        {" "}
        {(editPage || coverEdit) && application !== "renewal" && (
          <LabelButtonWrapper
            hierarchy="tertiary"
            label={cancelEditingBtnTextEurope || "Cancel editing"}
            id="cancel-edit-travel-europe"
            className={`${editQuoteStyle.cancelEditButton}`}
            onClick={handleCancelEdit}
            isDisabled={isQuoteDisabled}
          />
        )}
        {application === "renewal" && editPage && (
          <div>
            {backToRenewalsText && (
              <BackToRenewal
                label={backToRenewalsText ?? "back to renewals"}
                className="pb-spacing-m md:pb-[0px] top-[-2px] !px-[0px] mb-spacing-l md:!mb-spacing-m lg:mx-[0px]"
                onClick={() => setIsOpenUnsavedChangesModal(true)}
                data-testid="renewal-page-back-button"
              />
            )}
            <UnSavedChangesWarningMessage
              isOpen={isOpenUnsavedChangesModal}
              onContinueClick={() => {
                router.push(`/`);
              }}
              setIsOpen={setIsOpenUnsavedChangesModal}
            />
          </div>
        )}
        {current.uiElementControls?.locationFrom && (
          <LocationFromContainer
            questionOneText={locationFromContent?.questionOneText}
            questionOnePlaceHolder={locationFromContent?.questionOnePlaceHolder}
            setFromCountry={setFromCountry}
            buttonDisable={setButtonDisableFromLocation}
            locationFromCountreyClick={() => {
              const element1 = document.getElementById("paragaph-test-id");
              if (element1) {
                handleScrollToRefAnyoneVisiting(
                  actionButtonRef,
                  !isStickyFooter
                );
              } else {
                smoothScrollToRef(locationToHeaderRef);
              }
            }}
          />
        )}
        <h1
          className=" font-header-font lg:text-[39px] md:text-3xl text-[26px] lg:leading-[44.85px] 
                md:leading-[34.1px] leading-[29.9px] text-text-title md:text-left text-center"
          ref={locationToHeaderRef}
        >
          {amtQuestionOneText}
        </h1>
        {quoteLoading ? (
          <Loader />
        ) : (
          <ButtonGroup
            setSelectedAnswer={selectedCountry}
            selectedAnswer={countryId}
            answerArray={filteredTranslatedAnswers}
            location={QuestionTwo}
            type="to-location"
            isDisable={isDisable}
            onOptionClick={(id) => {
              if (current.uiElementControls?.locationFrom && id === 3) {
                setTimeout(() => {
                  handleScrollToRefAnyoneVisiting(
                    actionButtonRef,
                    !isStickyFooter
                  );
                }, 400);
              }
              if (
                !current.uiElementControls?.locationFrom &&
                (id === 2 || id === 3)
              ) {
                setTimeout(() => {
                  let targetRef;

                  if (isStickyFooter === true) {
                    targetRef = actionButtonRef;
                  } else {
                    targetRef = id === 3 ? worldwideHeaderRef : europeHeaderRef;
                  }

                  handleScrollToRefAnyoneVisiting(targetRef, !isStickyFooter);
                }, 500);
              }
            }}
          />
        )}
        <ConfirmationModal
          modalTitleText={modalTitleText}
          modalSubText={modalSubText}
          modalConfirmBtnText={modalConfirmBtnText}
          modalCancelBtnText={modalCancelBtnText}
          isOpen={modalOpen}
          modalConfirmBtnClick={modifyModalButtonClick}
        />
      </div>
    );
  };

  const renderQuestionTwoSection = () => {
    const renderVisitingEuropeSection = () => {
      const { amtQuestionTwoText, amtQuestionTwoEuropeDiscrip } = pageContent;
      const selectAnswerId = europeSelectedAnswer?.id;

      return (
        <TransformAnimation currentPage={usagePage}>
          <div className="md:mb-spacing-xxl md:mt-0 mt-spacing-l">
            <h1
              className=" font-header-font lg:text-[39px] md:text-3xl text-[26px] lg:leading-[44.85px] 
                  md:leading-[34.1px] leading-[29.9px] text-text-title md:text-left text-center"
              ref={europeHeaderRef}
            >
              {amtQuestionTwoText}
            </h1>
            <p
              className={` font-body-font md:text-[20px] mt-spacing-m mb-spacing-l text-[18px] 
                  md:leading-[23px] leading-[20.7px]  md:text-left text-center ${getFontClasses(
                    brand
                  )}`}
            >
              {amtQuestionTwoEuropeDiscrip}
            </p>
            {quoteLoading ? (
              <Loader />
            ) : (
              <ButtonGroup
                setSelectedAnswer={selectedEurope}
                selectedAnswer={selectAnswerId}
                answerArray={translatedEuropeAnswers}
                location={Europe}
                type="europe-location"
                isDisable={isDisable}
                onOptionClick={(id, dataTestId) => {
                  const buttonElement = document.querySelector(
                    `#${dataTestId}`
                  );
                  if (
                    buttonElement?.classList.contains(
                      "componnt_lib_toggle_btn_inactive"
                    )
                  ) {
                    handleScrollToRef(actionButtonRef, !isStickyFooter);
                  }
                }}
              />
            )}
          </div>
        </TransformAnimation>
      );
    };

    const renderVisitingWorldwideSection = () => {
      const { amtQuestionTwoText, amtQuestionTwoWorldWideDiscrip } =
        pageContent;
      const countryId = worldwideSelectedAnswer?.id;
      return (
        <TransformAnimation currentPage={usagePage}>
          <div className=" md:mb-spacing-xxl md:mt-0 mt-spacing-l">
            {" "}
            <h1
              className=" font-header-font lg:text-[39px] md:text-3xl text-[26px] lg:leading-[44.85px] 
            md:leading-[34.1px] leading-[29.9px] text-text-title md:text-left text-center"
              ref={worldwideHeaderRef}
            >
              {amtQuestionTwoText}
            </h1>
            <p
              className={` font-body-font md:text-[20px] mt-spacing-m mb-spacing-l text-[18px] 
                  md:leading-[23px] leading-[20.7px]  md:text-left text-center ${getFontClasses(
                    brand
                  )}`}
            >
              {amtQuestionTwoWorldWideDiscrip}
            </p>
            {quoteLoading ? (
              <Loader />
            ) : (
              <ButtonGroup
                setSelectedAnswer={selectedWorldWide}
                selectedAnswer={countryId}
                answerArray={translatedWorldwideAnswers}
                location={Worldwide}
                type="worldwide-location"
                isDisable={isDisable}
                onOptionClick={(id, dataTestId) => {
                  const buttonElement = document.querySelector(
                    `#${dataTestId}`
                  );
                  if (
                    buttonElement?.classList.contains(
                      "componnt_lib_toggle_btn_inactive"
                    )
                  ) {
                    handleScrollToRef(actionButtonRef, !isStickyFooter);
                  }
                }}
              />
            )}
          </div>
        </TransformAnimation>
      );
    };

    return (
      <div className=" w-full">
        {expatText && (
          <div className=" mt-spacing-m md:mb-spacing-xxl mb-0">
            <InlineInfoBox
              id="location-to-expat-info-box"
              content={<p>{expatText}</p>}
              style={{
                width: "100%",
                backgroundColor: "#EDF2FF",
                borderColor: "#006BD6",
                padding: "8px 16px 8px 16px",
                borderRadius: "8px",
                color: "#272427",
                fontSize: "16px",
                lineHeight: "24px",
              }}
            />
          </div>
        )}
        {selectedpolicyType !== AnnualMultiTrip &&
          application !== RenewalPage &&
          isClient && (
            <div
              className=" md:mb-spacing-xxl"
              data-testid="single-trip-support-content"
            >
              {" "}
              {(editPage || coverEdit) && (
                <LabelButtonWrapper
                  hierarchy="tertiary"
                  label={cancelEditingBtnTextWorldWide || "Cancel editing"}
                  id="cancel-edit-travel-worldwide"
                  className={`${editQuoteStyle.cancelEditButton}`}
                  onClick={handleCancelEdit}
                  isDisabled={isQuoteDisabled}
                />
              )}
              {current.uiElementControls?.locationFrom && (
                <LocationFromContainer
                  questionOneText={locationFromContent?.questionOneText}
                  questionOnePlaceHolder={
                    locationFromContent?.questionOnePlaceHolder
                  }
                  setFromCountry={setFromCountry}
                  buttonDisable={setButtonDisableFromLocation}
                  locationFromCountreyClick={() => {
                    const element1 =
                      document.getElementById("paragaph-test-id");
                    if (element1) {
                      handleScrollToRefAnyoneVisiting(
                        actionButtonRef,
                        !isStickyFooter
                      );
                    } else {
                      const element =
                        document.getElementById("ST-MAIN-HEADER-ID");
                      setTimeout(() => {
                        element?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }, 100);
                    }
                  }}
                />
              )}
              <h1
                id="ST-MAIN-HEADER-ID"
                className=" font-header-font  lg:text-[39px] md:text-3xl text-[26px] lg:leading-[44.85px] 
              md:leading-[34.1px] leading-[29.9px] text-text-title md:text-left text-center"
                ref={locationToSTHeaderRef}
              >
                {questionTwoText}
              </h1>
              {quoteLoading ? (
                <Loader />
              ) : (
                <button
                  id="scrollableInput"
                  data-testid="scrollable-input-id"
                  type="button"
                  onClick={handleScrollDown}
                  className={` ${styles.country_tag_search_wrapper} lg:w-[414px] md:w-[400px] w-full mx-auto border hover:border-2 !border-interactive-secondary-default min-h-[56px] mt-spacing-l focus-visible:outline-none`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleScrollDown();
                    }
                  }}
                  tabIndex={-1}
                >
                  {typeof window !== "undefined" && (
                    <TagSearchField
                      popularCountries={processCountryList(
                        popularCountryList ?? {}
                      )}
                      tagFieldPlaceholderClassName="!text-text-placeholder-secondary !font-semibold !font-body-font !text-[16px] leading-[24px]"
                      popularCountryTitle={popularCountryHeaderText}
                      popularCountryClassName={`text-[18px]  cursor-pointer ${getFontClasses(
                        brand
                      )} !font-normal !leading-[27px]`}
                      popularCountryHeaderClassName={`text-[16px] ${getFontClasses(
                        brand
                      )} !leading-[24px]`}
                      optionList={addressList}
                      width="200"
                      values={selectedSTCountry}
                      freeSolo={freeSoloTag}
                      $brand={brand}
                      noOptionsText={optionListText}
                      selectOnFocus={false}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      onChange={(event: unknown, values: any) => {
                        if (values && values.length > 0) {
                          // scrollInputToTop();
                          handleScrollToRef(actionButtonRef, !isStickyFooter);
                        }
                        if (values.length === 0) {
                          setSelectedSTCountry([]);
                        }

                        if (
                          typeof values[values.length - 1] !== "string" ||
                          values.length === 0
                        ) {
                          const duplicateCountry = findDuplicatesByProperty(
                            values,
                            "parentCode"
                          );

                          if (duplicateCountry.length === 0) {
                            const selectedCountryOnChange =
                              values.length > 0
                                ? values[values.length - 1].parentCode
                                : "";

                            let deleteFunction = false;
                            if (selectedSTCountry) {
                              if (selectedSTCountry.length > values.length) {
                                deleteFunction = true;
                              }
                            }
                            if (
                              !deleteFunction &&
                              selectedpolicyType === SingleTrip &&
                              checkCurentLocation(
                                fromCountryCode ??
                                  (current.brandSpecificParams
                                    ?.fromCountry as string),
                                selectedCountryOnChange || ""
                              )
                            ) {
                              if (values.length > 1) {
                                const modifiedValue = [...values];
                                modifiedValue.splice(-1, 1);
                                setSelectedSTCountry(modifiedValue as []);
                              } else {
                                setSelectedSTCountry([]);
                              }
                              setShowSameCountryModal(true);
                              const newArray = [values[values.length - 1]];
                              setSameCountrySelected(newArray as []);
                            } else {
                              setShowSameCountryModal(false);
                              tagSearchFieldOnChange(values as []);
                            }
                          } else {
                            setIsModalOpen(true);
                          }
                        } else if (values && values.length > 0) {
                          const modifiedValue = [...values];
                          modifiedValue.splice(-1, 1);
                          setSelectedSTCountry(modifiedValue as []);
                          tagSearchFieldOnChange(modifiedValue);
                        } else {
                          setSelectedSTCountry([]);
                        }
                      }}
                      onInputChange={(e: unknown, value: unknown) => {
                        const query = value as string;
                        setSelectedStateTwo(query);
                        scrollInputToTop();
                        if (query.length > 2) {
                          if (
                            addresses &&
                            addresses.length > 0 &&
                            query === valueFilter
                          ) {
                            setAddressList(generateTagSearchArray(addresses));
                          }
                          setValueFilter(query);
                        } else {
                          setFreeSoloTag(true);

                          setAddressList([]);
                        }
                        if (!query) {
                          setValueFilter(null);
                        }
                      }}
                      $hasValues={false}
                      $width=""
                      $alertState="success"
                      tagFieldPlaceholderText={questionTwoPlaceHolder}
                    />
                  )}
                </button>
              )}
              <Modal
                className={`date-picker-modal md:p-spacing-xxxl justify-center p-spacing-l ${
                  fcdoCountry ? "md:max-w-[416px]" : "md:max-w-[327px]"
                } 
              ${fcdoCountry ? "max-w-[327px]" : "max-w-[267px]"}   w-full`}
                isOpen={isModalOpen}
                onOpenChange={(e: boolean) => setIsModalOpen(e)}
                underlayStyles={{
                  zIndex: 9999,
                  background: "rgba(26, 26, 26, 0.6)",
                }}
                modalStyles={{
                  display: "flex",
                  background: "white",
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  borderRadius: 8,
                }}
              >
                <div>
                  <h1 className="text-text-title font-header-font md:text-3xl text-[23px] text-center md:leading-[34.1px] leading-[27.6px] md:mb-spacing-s mb-spacing-xs">
                    {fcdoCountry
                      ? props?.pageContent?.fcdoCountryHeaderText
                      : props?.pageContent?.sameCountryHeaderText ||
                        "Information"}
                  </h1>

                  <div className={` ${styleComponent.text_content}`}>
                    {fcdoCountry ? (
                      <RichText richText={fcdoCountrySubTextText ?? ""} />
                    ) : (
                      sameCountrySubTextText
                    )}
                  </div>
                  <div className="flex flex-row">
                    <TextButton
                      id="location-to-fcdo-country-button"
                      hierarchy="primary"
                      label={
                        fcdoCountry
                          ? fcdoCountryBtnText ?? "Cancel"
                          : sameCountryBtnText ?? "Cancel"
                      }
                      onClick={() => {
                        setFcdoCountry(false);
                        setIsModalOpen(false);
                      }}
                      className="m-0 h-[48px] text-text-on-primary font-lg leading-[18px] font-semibold "
                    />
                  </div>
                </div>
              </Modal>
            </div>
          )}

        {(selectedpolicyType === AnnualMultiTrip ||
          application === RenewalPage) &&
          renderQuestionTwoSectionAMT()}
        {selectedAMTEuropeWorldwide === Europe && renderVisitingEuropeSection()}
        {selectedAMTEuropeWorldwide === Worldwide &&
          renderVisitingWorldwideSection()}
      </div>
    );
  };

  return (
    <div
      data-testid="question-two-test-id"
      className={` rounded-radius-xxl 
      bg-surface-neutral-option-1 rounded-b-radius-xxl  
           md:py-spacing-xxl py-spacing-m px-spacing-m h-full`}
    >
      <div
        className=" lg:max-w-[414px] md:max-w-[400px] max-w-[unset] w-full mx-auto relative h-full 
      flex flex-col justify-between md:min-h-[225px]"
      >
        <TransformAnimation animationRef={formRef} currentPage={usagePage}>
          {renderQuestionTwoSection()}
        </TransformAnimation>

        <FadeAnimation animationRef={ctaRef}>
          <div className={`${editFinishStyle}`} ref={actionButtonRef}>
            <ActionButton
              page={usagePage}
              ctaButtonText={
                editPage && !coverEdit ? finishEditingBtnText : ctaButtonText
              }
              disableMain={
                buttonDisable ||
                (current.uiElementControls?.locationFrom
                  ? buttonDisableFromLocation
                  : false)
              }
              expatButton={expatStatus}
              isLoading={buttonLoading}
              showBackBtn={
                (application === RenewalPage && editPage === "true") ||
                (application !== RenewalPage && !editPage)
              }
              submitButtonClick={handleSubmitButton}
              backButtonClick={handleBackButton}
            />
          </div>
        </FadeAnimation>
      </div>

      {renderModal()}
      <ConfirmationsModal
        isOpen={editModal}
        handleConfirmBtnClick={() => {
          // router.push(`/${Quote}/?${QuoteEdit}=true`);
          router.push(current.edges?.cancelEdit ?? "");
          if (coverEdit) {
            resetQuestionData();
          }
        }}
        handleCancelmBtnClick={() => {
          setEditModal(false);
        }}
      />
    </div>
  );
}
export default withBrand(withApplication(LocationToContainer));
